export type USStateOption = {
  code: string;
  name: string;
};

export const US_STATES_48: USStateOption[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const stateCodeSet = new Set(US_STATES_48.map((state) => state.code));
const stateNameToCode = new Map(US_STATES_48.map((state) => [state.name.toLowerCase(), state.code]));

const extractPhoneDigits = (value: string): string => {
  const rawValue = String(value || '');
  const digits = rawValue.replace(/\D/g, '');
  if (!digits) return '';

  // If the user is typing with an explicit +1 prefix, always drop that country digit.
  if (/^\s*\+1/.test(rawValue) && digits.startsWith('1')) {
    return digits.slice(1, 11);
  }

  // Also normalize pasted 11-digit NANP numbers like 17061234567.
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }

  return digits.slice(0, 10);
};

export const formatUsPhoneInput = (value: string, previousValue = ''): string => {
  const previousDigits = extractPhoneDigits(previousValue);
  const rawNextDigits = extractPhoneDigits(value);
  const isDeleting = String(value || '').length < String(previousValue || '').length;
  const deletedFormattingOnly = isDeleting && rawNextDigits.length === previousDigits.length;
  const digits = deletedFormattingOnly ? previousDigits.slice(0, -1) : rawNextDigits;
  if (!digits) return '';

  if (digits.length <= 3) return `+1 (${digits}`;
  if (digits.length <= 6) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const isValidUsPhone = (value: string): boolean => /^\+1 \(\d{3}\) \d{3}-\d{4}$/.test(String(value || '').trim());

export const formatUsZipInput = (value: string): string => String(value || '').replace(/\D/g, '').slice(0, 5);

export const isValidUsZip = (value: string): boolean => /^\d{5}$/.test(String(value || '').trim());

export const normalizeUsStateCode = (value: string): string => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';

  const upper = trimmed.toUpperCase();
  if (stateCodeSet.has(upper)) return upper;

  return stateNameToCode.get(trimmed.toLowerCase()) || '';
};

export const isValidUsState = (value: string): boolean => stateCodeSet.has(normalizeUsStateCode(value));
